import { computed, onUnmounted, ref } from 'vue'
import { useStore } from 'vuex'

import { getAgentNotices, readAgentNotice } from '@/api/agentNotices'
import { useSoundSetting } from '@/composables/useUnreadAlerts'

// Rarer than the message poll: a supervisor's push is not a conversation
const POLL_INTERVAL = 10000

let audioContext = null

// Three low knocks: deliberately unlike the chime that announces a message
const playKnock = () => {
  const Context = window.AudioContext || window.webkitAudioContext
  if (!Context) return

  audioContext = audioContext || new Context()
  if (audioContext.state === 'suspended') audioContext.resume()

  const start = audioContext.currentTime
  ;[0, 0.18, 0.36].forEach(offset => {
    const oscillator = audioContext.createOscillator()
    const gain = audioContext.createGain()
    const at = start + offset

    oscillator.frequency.value = 320
    gain.gain.setValueAtTime(0.0001, at)
    gain.gain.exponentialRampToValueAtTime(0.2, at + 0.02)
    gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.16)
    oscillator.connect(gain).connect(audioContext.destination)
    oscillator.start(at)
    oscillator.stop(at + 0.17)
  })
}

export function useAgentNotices() {
  const store = useStore()
  const notices = ref([])
  const accountId = computed(
    () => store.getters['auth/getCurrentUser']?.accounts?.[0]?.id
  )
  let timer = null

  const current = computed(() => notices.value[0] || null)

  const poll = async () => {
    if (accountId.value) {
      try {
        const { data } = await getAgentNotices(accountId.value)
        const fresh = data.filter(
          notice => !notices.value.some(shown => shown.id === notice.id)
        )
        if (fresh.length) {
          notices.value = [...notices.value, ...fresh]
          const { soundEnabled } = useSoundSetting()
          if (soundEnabled.value) playKnock()
        }
      } catch (error) {
        // non-fatal, the next round retries
      }
    }
    timer = setTimeout(poll, POLL_INTERVAL)
  }

  const dismiss = async () => {
    const notice = current.value
    if (!notice) return

    notices.value = notices.value.filter(shown => shown.id !== notice.id)
    try {
      await readAgentNotice(accountId.value, notice.id)
    } catch (error) {
      // the notice is gone from the screen; a failed mark is retried next poll
    }
  }

  poll()
  onUnmounted(() => clearTimeout(timer))

  return { current, dismiss }
}
